import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ToggleGroup,
  ToggleGroupItem,
} from "web-design-system";
import type { FundBasketChartData } from "creso-core/types";
import { useId, useMemo, useState } from "react";
import { Area, ComposedChart, Line, XAxis, YAxis } from "recharts";
import type { TooltipProps } from "recharts";
import { format } from "date-fns";
import { useFundBasketAnalytics, useFundBasketChart } from "creso-core/hooks";
import { convertToCurrency } from "creso-core/utils";
import {
  BENCHMARK_OPTIONS,
  FundBasketBacktestChart,
} from "./fund-basket-backtest-chart";
import { WeightedReturnsTable } from "./basket-analytics";

const RANGE_OPTIONS = ["YTD", "1M", "3M", "1Y", "3Y", "5Y"] as const;
export type FundBasketReturnsRange = (typeof RANGE_OPTIONS)[number];

const BASKET_STROKE = "var(--chart-3)";
const BENCHMARK_STROKE = "var(--muted-foreground)";

const BASKET_LABEL = "Basket portfolio";

const chartConfig = {
  basket: {
    label: BASKET_LABEL,
    color: BASKET_STROKE,
  },
  benchmark: {
    label: "Benchmark",
    color: BENCHMARK_STROKE,
  },
} satisfies ChartConfig;

type ChartPoint = {
  date: string;
  basket?: number;
  benchmark?: number;
  basket_raw?: number;
  benchmark_raw?: number;
  basket_abs?: number;
  benchmark_abs?: number;
};

function normalizeRange(period: string | undefined): FundBasketReturnsRange {
  if (!period) return "1Y";
  const key = period.trim().toUpperCase();
  return RANGE_OPTIONS.includes(key as FundBasketReturnsRange)
    ? (key as FundBasketReturnsRange)
    : "1Y";
}

function mergeChartSeries(
  payload: FundBasketChartData | undefined
): ChartPoint[] {
  if (!payload) return [];
  const series = payload.series ?? [];
  return [...series]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((p) => ({
      date: p.date,
      basket: p.basket?.value,
      benchmark: p.benchmark?.value,
      basket_raw: p.basket?.raw_value,
      benchmark_raw: p.benchmark?.raw_value,
      basket_abs: p.basket?.absolute_return,
      benchmark_abs: p.benchmark?.absolute_return,
    }));
}

export function FundBasketReturnsChart({
  id,
  chartData,
}: {
  id: number;
  chartData: FundBasketChartData;
}) {
  return (
    <Tabs defaultValue="backtest">
      <div className="flex items-center justify-between">
        <span className="text-base font-semibold">Returns</span>
        <TabsList>
          <TabsTrigger value="backtest">Returns Backtest</TabsTrigger>
          <TabsTrigger value="historical">Historical Returns</TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="backtest">
        <FundBasketBacktestChart id={id} />
      </TabsContent>

      <TabsContent value="historical">
        <HistoricalReturns id={id} chartData={chartData} />
      </TabsContent>
    </Tabs>
  );
}

function HistoricalReturns({
  id,
  chartData,
}: {
  id: number;
  chartData: FundBasketChartData;
}) {
  const uid = useId().replace(/:/g, "");
  const [range, setRange] = useState<FundBasketReturnsRange>(() =>
    normalizeRange(chartData.period)
  );
  const [benchmark, setBenchmark] = useState("NIFTY 500");

  const { data: analytics } = useFundBasketAnalytics({
    id,
    benchmark,
  });

  const { data } = useFundBasketChart({ id, period: range, benchmark });

  const seriesSource = data ?? chartData;
  const chartPoints = useMemo(
    () => mergeChartSeries(seriesSource),
    [seriesSource]
  );

  const hasChartPoints = chartPoints.length > 0;

  const latestInceptionDate = useMemo(() => {
    const schemes = analytics?.schemes ?? [];
    if (schemes.length === 0) return null;
    return schemes.reduce(
      (max, s) => Math.max(max, new Date(s.fund.inception_date).getTime()),
      0
    );
  }, [analytics?.schemes]);

  const { chartStartDate, chartEndDate } = useMemo(() => {
    const endDate = new Date();
    if (!latestInceptionDate) {
      return {
        chartStartDate: undefined,
        chartEndDate: format(endDate, "yyyy-MM-dd"),
      };
    }
    let requestedStart: Date;
    switch (range) {
      case "YTD":
        requestedStart = new Date(endDate.getFullYear(), 0, 1);
        break;
      case "1M":
        requestedStart = new Date(new Date().setDate(endDate.getDate() - 30));
        break;
      case "3M":
        requestedStart = new Date(new Date().setDate(endDate.getDate() - 90));
        break;
      case "1Y":
        requestedStart = new Date(new Date().setDate(endDate.getDate() - 365));
        break;
      case "3Y":
        requestedStart = new Date(
          new Date().setDate(endDate.getDate() - 365 * 3)
        );
        break;
      case "5Y":
        requestedStart = new Date(
          new Date().setDate(endDate.getDate() - 365 * 5)
        );
        break;
      default:
        requestedStart = new Date(new Date().setDate(endDate.getDate() - 365));
    }
    const startDate = new Date(
      Math.max(latestInceptionDate, requestedStart.getTime())
    );
    return {
      chartStartDate: format(startDate, "MMM dd, yyyy"),
      chartEndDate: format(endDate, "MMM dd, yyyy"),
    };
  }, [latestInceptionDate, range]);

  const benchmarkLabel =
    BENCHMARK_OPTIONS.find((opt) => opt.value === benchmark)?.label ??
    benchmark;

  const formatXAxisTick = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "MMM yy");
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col rounded-lg border border-border">
        <div className="flex flex-row items-stretch border-b border-border p-5 pt-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">Time period</span>
            <ToggleGroup
              type="single"
              value={range}
              className="h-9 w-fit"
              onValueChange={(v) => {
                if (v) setRange(v as FundBasketReturnsRange);
              }}
            >
              {RANGE_OPTIONS.map((option) => (
                <ToggleGroupItem key={option} value={option} size="xs">
                  {option}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {chartStartDate && chartEndDate && (
              <span className="text-xs text-muted-foreground">
                Date overlap &ndash; {chartStartDate} to {chartEndDate}
              </span>
            )}
          </div>

          <div className="mx-8 w-px bg-border" />

          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">
              Compare against
            </span>
            <Select value={benchmark} onValueChange={setBenchmark}>
              <SelectTrigger className="h-9 w-60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BENCHMARK_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {hasChartPoints ? (
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-100 p-5 pb-0 w-full"
          >
            <ComposedChart
              data={chartPoints}
              margin={{ top: 8, right: 8, bottom: 28, left: 4 }}
            >
              <defs>
                <linearGradient
                  id={`basketFill-${uid}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={BASKET_STROKE}
                    stopOpacity={0.4}
                  />
                  <stop
                    offset="95%"
                    stopColor={BASKET_STROKE}
                    stopOpacity={0.05}
                  />
                </linearGradient>
              </defs>
              <YAxis hide domain={["auto", "auto"]} />
              <XAxis
                dataKey="date"
                domain={["dataMin", "dataMax"]}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={formatXAxisTick}
                interval="preserveStartEnd"
                minTickGap={40}
              />
              <ChartTooltip
                cursor={false}
                content={<ReturnsTooltip benchmarkLabel={benchmarkLabel} />}
              />
              <Line
                type="monotone"
                dataKey="benchmark"
                stroke={BENCHMARK_STROKE}
                strokeWidth={1.5}
                strokeDasharray="1 4"
                strokeLinecap="round"
                dot={false}
                connectNulls
                isAnimationActive
              />
              <Area
                type="monotone"
                dataKey="basket"
                stroke={BASKET_STROKE}
                strokeWidth={2}
                fill={`url(#basketFill-${uid})`}
                dot={false}
                connectNulls
                isAnimationActive
              />
            </ComposedChart>
          </ChartContainer>
        ) : (
          <div className="flex h-[200px] items-center justify-center bg-muted text-sm text-muted-foreground border-b border-border/50">
            No chart data for this basket yet.
          </div>
        )}

        {analytics?.weighted_returns && (
          <WeightedReturnsTable
            weighted_returns={analytics?.weighted_returns}
            benchmarkLabel={benchmarkLabel}
          />
        )}
      </div>
    </div>
  );
}

function ReturnsTooltip({
  active,
  payload,
  benchmarkLabel,
}: TooltipProps<number, string> & { benchmarkLabel: string }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as ChartPoint | undefined;
  if (!point) return null;

  const parsed = Date.parse(point.date);
  const formattedDate = Number.isNaN(parsed)
    ? point.date
    : format(parsed, "MMM dd, yyyy");

  return (
    <div className="border-border/50 bg-background min-w-56 rounded-md border p-3 text-xs shadow">
      <div className="mb-3 text-xs font-semibold text-foreground">
        {formattedDate}
      </div>
      <div className="flex flex-col gap-3">
        <TooltipSeriesRow
          color={BASKET_STROKE}
          name={BASKET_LABEL}
          subLabel="Weighted NAV"
          absoluteReturn={point.basket_abs}
          rawValue={point.basket_raw}
        />
        <TooltipSeriesRow
          color={BENCHMARK_STROKE}
          name={benchmarkLabel}
          subLabel="Index value"
          absoluteReturn={point.benchmark_abs}
          rawValue={point.benchmark_raw}
        />
      </div>
    </div>
  );
}

function TooltipSeriesRow({
  color,
  name,
  subLabel,
  absoluteReturn,
  rawValue,
}: {
  color: string;
  name: string;
  subLabel: string;
  absoluteReturn: number | undefined;
  rawValue: number | undefined;
}) {
  const hasReturn = typeof absoluteReturn === "number";
  const hasRaw = typeof rawValue === "number";
  const sign = hasReturn
    ? absoluteReturn > 0
      ? "+"
      : absoluteReturn < 0
        ? "-"
        : ""
    : "";

  return (
    <div className="flex items-start gap-1">
      <span
        aria-hidden
        className="mt-0.75 h-2.5 w-2.5 shrink-0 rounded-[3px]"
        style={{ backgroundColor: color }}
      />
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex items-center justify-between gap-6">
          <span className="text-foreground">{name}</span>
          {hasReturn && (
            <span
              data-sign={sign}
              className={cn(
                "font-medium tabular-nums",
                "data-[sign='+']:text-constructive data-[sign='-']:text-destructive",
                sign === "" && "text-muted-foreground"
              )}
            >
              {absoluteReturn.toFixed(2)}%
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-xs  text-muted-foreground">{subLabel}</span>
          {hasRaw && (
            <span className="text-xs">{convertToCurrency(rawValue, 0)}</span>
          )}
        </div>
      </div>
    </div>
  );
}
